import { useCallback, useEffect, useMemo, useState } from "react";
import { getApiBase } from "../apiBase";

const API_BASE = getApiBase();

function formatStatusLabel(status) {
  const value = String(status || "").trim().toLowerCase();
  if (value === "verified") {
    return "Linked";
  }
  if (value === "pending") {
    return "Waiting for code";
  }
  return "Not linked";
}

function formatWhen(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function WhatsAppLinkPanel({ authToken, phoneValue }) {
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [links, setLinks] = useState([]);
  const [linkingEnabled, setLinkingEnabled] = useState(false);
  const [importsEnabled, setImportsEnabled] = useState(false);
  const [businessPhoneNumber, setBusinessPhoneNumber] = useState("");
  const [localError, setLocalError] = useState("");

  const loadStatus = useCallback(
    async ({ silent = false } = {}) => {
      if (!authToken) {
        return;
      }
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setLocalError("");
      try {
        const res = await fetch(`${API_BASE}/api/integrations/whatsapp/status`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.message || "Could not load WhatsApp status.");
        }
        setLinks(Array.isArray(data.links) ? data.links : []);
        setLinkingEnabled(Boolean(data.linkingEnabled));
        setImportsEnabled(Boolean(data.importsEnabled));
        setBusinessPhoneNumber(typeof data.businessPhoneNumber === "string" ? data.businessPhoneNumber : "");
      } catch (error) {
        setLocalError(error.message || "Could not load WhatsApp status.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authToken]
  );

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const pendingLinks = useMemo(
    () => links.filter((item) => String(item.status || "").toLowerCase() === "pending"),
    [links]
  );

  useEffect(() => {
    if (!authToken || pendingLinks.length === 0) {
      return undefined;
    }
    const id = window.setInterval(() => {
      loadStatus({ silent: true });
    }, 15000);
    return () => window.clearInterval(id);
  }, [authToken, loadStatus, pendingLinks.length]);

  const startLink = async () => {
    if (!authToken || !phoneValue) {
      return;
    }
    setStarting(true);
    setLocalError("");
    try {
      const res = await fetch(`${API_BASE}/api/integrations/whatsapp/link/start`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ phone: phoneValue }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Could not start WhatsApp linking.");
      }
      await loadStatus({ silent: true });
    } catch (error) {
      setLocalError(error.message || "Could not start WhatsApp linking.");
    } finally {
      setStarting(false);
    }
  };

  const unlinkPhone = async (phone) => {
    if (!authToken || !phone) {
      return;
    }
    setLocalError("");
    try {
      const res = await fetch(
        `${API_BASE}/api/integrations/whatsapp/links/${encodeURIComponent(phone)}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Could not remove WhatsApp link.");
      }
      await loadStatus({ silent: true });
    } catch (error) {
      setLocalError(error.message || "Could not remove WhatsApp link.");
    }
  };

  return (
    <section className="profile-section whatsapp-link-section" aria-labelledby="profile-whatsapp-heading">
      <header>
        <h2 id="profile-whatsapp-heading">WhatsApp import</h2>
        <p>
          Link your WhatsApp number so you can send memory photos or videos straight into AfterMe
          for review, tagging, and release timing.
        </p>
      </header>

      {loading ? (
        <p className="profile-field-hint" role="status">
          Loading WhatsApp status…
        </p>
      ) : null}

      {!loading && !linkingEnabled ? (
        <p className="profile-field-hint">
          WhatsApp linking is not configured on this API yet. Once the webhook and integration
          settings are enabled, this panel will show your link code and import status here.
        </p>
      ) : null}

      {localError ? (
        <div className="profile-alert error" role="status">
          {localError}
        </div>
      ) : null}

      {!loading && linkingEnabled ? (
        <>
          <div className="whatsapp-link-summary">
            <div>
              <span className="whatsapp-summary-label">AfterMe number</span>
              <strong>{businessPhoneNumber || "Configured on your AfterMe workspace"}</strong>
            </div>
            <div>
              <span className="whatsapp-summary-label">Import status</span>
              <strong>{importsEnabled ? "Ready for media imports" : "Linking only"}</strong>
            </div>
            <button
              type="button"
              className="secondary whatsapp-inline-refresh"
              onClick={() => loadStatus({ silent: true })}
              disabled={refreshing}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          <div className="whatsapp-link-steps">
            <p className="profile-field-hint">
              Use the phone number above in your profile. Generate a code, then send that code as
              a WhatsApp message from the same number to confirm the link.
            </p>
            <button
              type="button"
              className="small-btn"
              onClick={startLink}
              disabled={starting || !phoneValue}
            >
              {starting ? "Generating…" : "Generate link code"}
            </button>
          </div>

          {!phoneValue ? (
            <p className="profile-field-hint">
              Add your phone number above first so AfterMe knows which WhatsApp sender to link.
            </p>
          ) : (
            <p className="whatsapp-link-phone">
              Linking phone: <strong>{phoneValue}</strong>
            </p>
          )}

          {links.length > 0 ? (
            <ul className="whatsapp-link-list">
              {links.map((link) => (
                <li key={link.phone} className="whatsapp-link-card">
                  <div className="whatsapp-link-card-head">
                    <div>
                      <strong>{link.phone}</strong>
                      <span
                        className={`whatsapp-link-badge whatsapp-link-badge-${String(link.status || "").toLowerCase()}`}
                      >
                        {formatStatusLabel(link.status)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => unlinkPhone(link.phone)}
                    >
                      Remove link
                    </button>
                  </div>

                  {link.linkCode ? (
                    <div className="whatsapp-link-code-card">
                      <span className="whatsapp-summary-label">Send this code on WhatsApp</span>
                      <code>{link.linkCode}</code>
                      {link.linkCodeExpiresAt ? (
                        <span className="profile-field-hint">
                          Expires {formatWhen(link.linkCodeExpiresAt)}
                        </span>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="whatsapp-link-meta">
                    {link.verifiedAt ? (
                      <span>Verified {formatWhen(link.verifiedAt)}</span>
                    ) : null}
                    {link.lastInboundAt ? (
                      <span>Last inbound {formatWhen(link.lastInboundAt)}</span>
                    ) : null}
                    {link.lastImportedAt ? (
                      <span>Last import {formatWhen(link.lastImportedAt)}</span>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="profile-field-hint">
              No WhatsApp number is linked yet. Generate a code when you are ready to connect it.
            </p>
          )}
        </>
      ) : null}
    </section>
  );
}
